import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { vi, enGB } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, InputAdornment, useMediaQuery } from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
import apis from '@src/apis';
import CalendarMonthRounded from '@src/assets/icons/CalendarMonthRounded.svg';
import InactiveCalendarMonthRounded from '@src/assets/icons/InactiveCalendarMonthRounded.svg';
import CustomAdapter from '@src/components/CustomDatePickerRange/CustomDateAdapter';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import CommonButton from '@src/new-components/Button';
import { LANGUAGE } from '@src/constants';
import i18n from '@src/languages';
import { useTheme } from '@mui/material/styles';
import actions from '@src/redux/actions';
import CommonInput from '@src/new-components/Input/CommonInput';
import DropdownCustom from '@src/new-components/Dropdown';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import {
  DURATION_TOKEN_TYPE,
  MAX_APP_NAME_LENGTH,
} from '@src/constants/apiApp';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { FEATURE } from '@src/constants/package';
import useUserInfo from '@src/hooks/useTtsUser';
import {
  StyledCreateAppDialogAction,
  StyledCreateAppDialogContent,
} from './index.style';
import UpgradeApiAccessPopup from '../ListApps/UpgradeApiAccessPopup';

const INITIAL_KEY = {
  name: '',
  duration: DURATION_TOKEN_TYPE.FOREVER.value,
  expiresAt: null,
};

const CreateApp = ({ open, page, onClose, onFetchApps, onSetPaging }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { language } = i18n;
  const dispatch = useDispatch();

  const [openPopupLockApiIntegration, setOpenPopupLockApiIntegration] =
    useState(false);

  const { user } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();

  const { getFeatureValue } = useFeatureFlags();
  const studioTtsApiEnabled = getFeatureValue(FEATURE_KEYS.STUDIO_TTS_API, {
    userId: user?.id,
    email: user?.email,
  });

  const [app, setApp] = useState(INITIAL_KEY);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const isAppNameValid = useMemo(
    () => app.name.length <= MAX_APP_NAME_LENGTH,
    [app.name],
  );

  const handleClose = () => {
    setApp(INITIAL_KEY);
    setOpenDatePicker(false);
    onClose();
    setErrors({});
    setLoading(false);
  };

  const handleOpenDatePicker = () => {
    if (app.duration === DURATION_TOKEN_TYPE.FOREVER.value) return;
    setOpenDatePicker(true);
  };

  const handleCloseDatePicker = () => {
    setOpenDatePicker(false);
  };

  const handlePasteAppName = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    setApp({ ...app, name: text });
    setErrors({ ...errors, name: null });
  };

  const handleChangeApp = (event) => {
    const { name, value } = event.target;

    switch (name) {
      case 'duration': {
        let expiresAt = moment().add(value, 'days');
        if (value === DURATION_TOKEN_TYPE.FOREVER.value) expiresAt = null;
        if (app?.expiresAt && value === DURATION_TOKEN_TYPE.CUSTOM.value)
          expiresAt = app.expiresAt;
        setApp({ ...app, [name]: value, expiresAt });
        setErrors({ ...errors, [name]: null });
        break;
      }

      case 'expiresAt': {
        if (app?.duration !== DURATION_TOKEN_TYPE.CUSTOM.value) {
          setApp({
            ...app,
            expiresAt: value,
            duration: DURATION_TOKEN_TYPE.CUSTOM.value,
          });
        } else {
          setApp({ ...app, expiresAt: value });
        }
        setOpenDatePicker(false);
        setErrors({ ...errors, [name]: null });
        break;
      }

      default: {
        setApp({ ...app, [name]: value });
        setErrors({ ...errors, [name]: null });
        break;
      }
    }
  };

  const handleClosePopupLockApiIntegration = () => {
    setOpenPopupLockApiIntegration(false);
  };

  const validateKey = () => {
    const { name } = app;
    let validateError = {};

    if (!name.trim())
      validateError = { ...validateError, name: t('appNameRequired') };

    if (name && name.length > MAX_APP_NAME_LENGTH)
      validateError = {
        ...validateError,
        name: t('appNameTooLong', { max: MAX_APP_NAME_LENGTH }),
      };

    setErrors(validateError);
    return Object.keys(validateError).length === 0;
  };

  const handleCreateApi = async () => {
    const { apiPackage = {} } = user;
    const { packageCode = '', packageExpiryDate = '' } = apiPackage;
    if (!studioTtsApiEnabled) {
      if (!packageCode) {
        dispatch(
          actions.noti.push({
            severity: 'warning',
            message: 'noApiPackage',
          }),
        );
        return;
      }

      const isExpiredPackage =
        packageExpiryDate && moment().isAfter(packageExpiryDate);
      if (isExpiredPackage) {
        dispatch(
          actions.noti.push({
            severity: 'warning',
            message: 'expiredPackage',
          }),
        );
        return;
      }
    }

    if (studioTtsApiEnabled) {
      const hasTtsApiFeature = ttsUser?.features?.includes(FEATURE.TTS_API);
      const isPackageExpired =
        user?.packageExpiryDate && moment().isAfter(user?.packageExpiryDate);
      if (!hasTtsApiFeature || isPackageExpired) {
        handleClose();
        setOpenPopupLockApiIntegration(true);
        return;
      }
    }

    const isValid = validateKey();
    if (!isValid) return;

    setLoading(true);

    const { name, expiresAt } = app;
    const respondData = await apis.apps.createApp({
      name,
      expiresAt,
    });

    if (respondData?.status) {
      dispatch(
        actions.noti.push({
          severity: 'success',
          message: 'createAppSuccess',
        }),
      );
      const { token } = respondData.result?.app || {};
      setApp({ ...app, token });

      if (page === 0) onFetchApps();
      else onSetPaging((prev) => ({ ...prev, page: 0 }));

      handleClose();
    } else {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'createAppFailure',
        }),
      );
      setLoading(false);
    }
  };

  const displayDuration = () => {
    const duration = Object.values(DURATION_TOKEN_TYPE).find(
      (item) => item.value === app.duration,
    );
    return duration?.label ? (
      <Typography variant="body-14-regular" color={COLOR.label2[100]}>
        {t(duration.label)}
      </Typography>
    ) : (
      <Typography variant="body-14-regular" color={COLOR.label2[100]}>
        {t('expireDays', { number: app.duration })}
      </Typography>
    );
  };

  const renderDialogContent = () => (
    <StyledCreateAppDialogContent>
      <div className="app-info">
        <div className="info-item">
          <Typography variant="body-14-medium" color={COLOR.label2[100]}>
            {t('appNameLabel')}
          </Typography>
          <CommonInput
            value={app.name}
            name="name"
            onChange={handleChangeApp}
            error={errors.name}
            helperText={errors.name}
            endAdornment={
              <InputAdornment position="end" sx={{ marginLeft: 'auto' }}>
                <Typography
                  variant="body-14-regular"
                  color={
                    !isAppNameValid
                      ? COLOR.functionalRed.default
                      : COLOR.label2[100]
                  }
                >
                  {app.name.length}
                </Typography>
                <Typography variant="body-14-regular" color={COLOR.label2[100]}>
                  /{MAX_APP_NAME_LENGTH}
                </Typography>
              </InputAdornment>
            }
            placeholder={t('inputAppNameApi')}
            onPaste={handlePasteAppName}
            sx={{
              minHeight: '40px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                height: '40px',
                ...(errors.name && {
                  '& fieldset': {
                    borderColor: `${COLOR.functionalRed.default} !important`,
                  },
                  '&:hover fieldset': {
                    borderColor: `${COLOR.functionalRed.default} !important`,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: `${COLOR.functionalRed.default} !important`,
                  },
                }),
              },
              '& .MuiOutlinedInput-input': {
                padding: '8.5px 14px',
              },
            }}
          />
        </div>
        <div className="info-item">
          <Typography variant="body-14-medium" color={COLOR.label2[100]}>
            {t('appExpiration')}
          </Typography>
          <DropdownCustom
            variant="app-duration"
            options={Object.values(DURATION_TOKEN_TYPE).map((item) => ({
              label: item?.label ? (
                <Typography variant="body-14-regular" color={COLOR.label2[100]}>
                  {t(item.label)}
                </Typography>
              ) : (
                <Typography variant="body-14-regular" color={COLOR.label2[100]}>
                  {t('expireDays', { number: item.value })}
                </Typography>
              ),
              value: item.value,
            }))}
            onChange={(value) =>
              handleChangeApp({ target: { name: 'duration', value } })
            }
            title={displayDuration()}
            value={app.duration}
          />
        </div>
      </div>
      <div className="date-item">
        <Typography variant="body-14-medium" color={COLOR.label2[100]}>
          {t('apiAppExpireDate')}
        </Typography>
        <LocalizationProvider
          dateAdapter={CustomAdapter}
          locale={language === LANGUAGE.VN ? vi : enGB}
        >
          <DatePicker
            open={openDatePicker}
            disabled={app.duration === DURATION_TOKEN_TYPE.FOREVER.value}
            disablePast
            name="expiresAt"
            inputFormat="dd/MM/yyyy"
            value={app.expiresAt}
            onChange={(value) =>
              handleChangeApp({ target: { name: 'expiresAt', value } })
            }
            PopperProps={{
              placement: 'bottom-end',
            }}
            onClose={handleCloseDatePicker}
            onOpen={handleOpenDatePicker}
            renderInput={(params) => {
              const { InputProps, ...otherParams } = params;
              return (
                <CommonInput
                  disabled={app.duration === DURATION_TOKEN_TYPE.FOREVER.value}
                  name="expiresAt"
                  size="small"
                  fullWidth
                  {...otherParams}
                  onClick={handleOpenDatePicker}
                  InputProps={{
                    endAdornment: (
                      <>
                        <IconButton
                          disabled={
                            app.duration === DURATION_TOKEN_TYPE.FOREVER.value
                          }
                          size="small"
                          onClick={handleOpenDatePicker}
                        >
                          <div style={{ width: '20px', height: '20px' }}>
                            <img
                              style={{ width: '100%', height: '100%' }}
                              src={
                                app.duration ===
                                DURATION_TOKEN_TYPE.FOREVER.value
                                  ? InactiveCalendarMonthRounded
                                  : CalendarMonthRounded
                              }
                              alt="calendar"
                            />
                          </div>
                        </IconButton>
                      </>
                    ),
                  }}
                  sx={{
                    height: '40px',
                    cursor:
                      app.duration === DURATION_TOKEN_TYPE.FOREVER.value
                        ? 'not-allowed'
                        : 'default',
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      backgroundColor:
                        app.duration === DURATION_TOKEN_TYPE.FOREVER.value
                          ? COLOR.secondary[40]
                          : 'transparent',
                      cursor:
                        app.duration === DURATION_TOKEN_TYPE.FOREVER.value
                          ? 'not-allowed'
                          : 'text',
                    },
                    '& .MuiOutlinedInput-input': {
                      cursor:
                        app.duration === DURATION_TOKEN_TYPE.FOREVER.value
                          ? 'not-allowed'
                          : 'text',
                    },
                  }}
                />
              );
            }}
          />
        </LocalizationProvider>
      </div>
    </StyledCreateAppDialogContent>
  );

  const renderDialogAction = () => (
    <StyledCreateAppDialogAction>
      <div className="button-1">
        <CommonButton
          variant="outlined"
          onClick={handleClose}
          customStyle={{
            height: '40px',
            padding: '8px 24px',
          }}
        >
          <Typography variant="body-14-semibold" color={COLOR.label2[100]}>
            {t('cancelCreateAppApi')}
          </Typography>
        </CommonButton>
      </div>
      <div className="button-1">
        <CommonButton
          variant="contained"
          onClick={handleCreateApi}
          disabled={loading || !isAppNameValid}
          customStyle={{
            height: '40px',
            padding: '8px 24px',
          }}
        >
          <Typography variant="body-14-semibold" color={COLOR.label2[100]}>
            {t('createAppApi')}
          </Typography>
        </CommonButton>
      </div>
    </StyledCreateAppDialogAction>
  );

  return (
    <>
      <CommonDialog
        open={open}
        disableAutoClose
        onClose={handleClose}
        contentComponent={renderDialogContent()}
        actionComponent={renderDialogAction()}
        renderTitle={
          isMobile ? (
            <Typography variant="body-16-bold" color={COLOR.darkGray}>
              {t('createAppDialogTitle')}
            </Typography>
          ) : (
            <Typography variant="body-20-bold" color={COLOR.darkGray}>
              {t('createAppDialogTitle')}
            </Typography>
          )
        }
      />
      {openPopupLockApiIntegration && (
        <UpgradeApiAccessPopup
          open={openPopupLockApiIntegration}
          onClose={handleClosePopupLockApiIntegration}
        />
      )}
    </>
  );
};

export default CreateApp;
